import { ROUTES } from './constants';
import {
  getSaenggibuPeriodState,
  isSaenggibuTabShown
} from '#/lib/saenggibu/period';

import { Icons } from '#/components/icons';
import { localizePath, splitLocale, type Locale } from '#/i18n/config';
import { MENU_LABELS_EN } from '#/i18n/menu-labels';
import { ADMIN_PERMISSIONS, USER_ROLE_PERMISSIONS } from './admin-role';
// import {
//   ADMIN_PERMISSIONS,
//   ADMIN_ROLE_PERMISSIONS,
//   SEATTING_ADMIN_PERMISSIONS
// } from './admin-role';

export interface MenuItem {
  imgURL?: any;
  href: string;
  label: string;
  active: boolean;
  children?: MenuItem[];
  permissions: string[];
  default?: boolean;
  manage?: boolean;
  admin?: boolean;
  hide?: boolean;
  /** 헤더에서 하위 항목을 드롭다운으로 펼칠지. (main-nav / mobile-main-nav 만 사용) */
  dropdown?: boolean;
}

/** 영어 화면이면 라벨을 영어로, 주소에 /en 접두어를 붙인다. 한국어는 그대로 돌려준다. */
function localizeMenu(items: MenuItem[], locale: Locale): MenuItem[] {
  if (locale === 'ko') return items;
  return items.map((item) =>
    item.admin
      ? item
      : {
          ...item,
          label: MENU_LABELS_EN[item.label] ?? item.label,
          href: item.href.startsWith('/')
            ? localizePath(item.href, locale)
            : item.href,
          children: item.children && localizeMenu(item.children, locale)
        }
  );
}

/**
 * saenggibuSeen: 이 세션에서 /saenggibu 주소로 들어온 적이 있는지(숨김 공개 중 탭 표시용).
 * 정하지 않으면 false 라서 숨김 공개 중에는 생활기록부 항목이 빠진다.
 */
export const MenuItems = (
  params: any,
  pathname: string,
  locale: Locale = 'ko',
  saenggibuSeen: boolean = false
): MenuItem[] =>
  localizeMenu(
    buildMenuItems(params, splitLocale(pathname).rest, saenggibuSeen),
    locale
  );

const buildMenuItems = (
  params: any,
  pathname: string,
  saenggibuSeen: boolean
): MenuItem[] => [
  {
    imgURL: Icons.update,
    href: ROUTES.CORRECT,
    // 헤더에 항상 노출되는 대표 진입점이라 라벨을 고정한다. 종전에는 현재 경로에
    // 따라 라벨이 바뀌었는데, 그때는 이 항목이 헤더에 아예 안 보였다.
    label: '맞춤법 검사',
    active:
      pathname === '/' ||
      [
        ROUTES.CORRECT,
        ROUTES.ANALYZE,
        ROUTES.TOKENIZE,
        ROUTES.DICT_SEARCH,
        ROUTES.CUSTOM_DICT,
        ROUTES.SAENGGIBU
      ].some((route) => pathname.includes(route)),
    permissions: [],
    // 깊은 링크(/qa/... 등)로 들어온 방문자도 주력 기능을 찾을 수 있게 상시 노출한다.
    default: true,
    dropdown: true,
    children: [
      {
        href: ROUTES.CORRECT,
        label: '바른 맞춤법 검사',
        active: pathname === '/' || pathname.includes(ROUTES.CORRECT),
        permissions: []
      },
      // 파일(엑셀) 맞춤법 검사 — 잠정 폐쇄(/correct-file 라우팅 닫음). 개선 후 재개.
      // {
      //   href: ROUTES.CORRECTFILE,
      //   label: '파일(엑셀) 맞춤법 검사',
      //   active: pathname.includes(ROUTES.CORRECTFILE),
      //   permissions: []
      // },
      // 공개 기간(2026-11-03~2027-02-28)에만 보인다 (#316). 숨김 공개 중에는 주소로 들어온 세션에만 보인다.
      ...(isSaenggibuTabShown(
        getSaenggibuPeriodState(new Date()),
        saenggibuSeen
      )
        ? [
            {
              href: ROUTES.SAENGGIBU,
              label: '2026 생활기록부 맞춤법 검사',
              active: pathname.includes(ROUTES.SAENGGIBU),
              permissions: []
            }
          ]
        : []),
      {
        href: ROUTES.ANALYZE,
        label: '형태소 분석',
        active: pathname.includes(ROUTES.ANALYZE),
        permissions: []
      },
      {
        href: ROUTES.TOKENIZE,
        label: '단어 분절(토크나이저)',
        active: pathname.includes(ROUTES.TOKENIZE),
        permissions: []
      },
      {
        // 우리말샘 음소 검색 (baikal-nlp#1899) — 자소 조건으로 사전을 뒤진다.
        href: ROUTES.DICT_SEARCH,
        label: '우리말샘 음소 검색',
        active: pathname.includes(ROUTES.DICT_SEARCH),
        permissions: []
      },

      {
        href: ROUTES.CUSTOM_DICT,
        label: '사용자 사전',
        active: pathname.includes(ROUTES.CUSTOM_DICT),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.download,
    href: ROUTES.HOME,
    label: '바른 소개',
    active: pathname.includes(ROUTES.HOME),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.HOME,
        label: '바른 소개',
        active: pathname.includes(ROUTES.HOME),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.download,
    href: ROUTES.DOWNNLOAD,
    label: '다운로드',
    active: pathname.includes(ROUTES.DOWNNLOAD),
    permissions: USER_ROLE_PERMISSIONS.map((i) => i.value),
    default: false,
    children: [
      {
        href: ROUTES.DOWNNLOAD,
        label: '다운로드',
        active: pathname.includes(ROUTES.DOWNNLOAD),
        permissions: USER_ROLE_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.DOWNNLOAD_VERSION,
        label: '이전 버전',
        active: pathname.includes(ROUTES.DOWNNLOAD_VERSION),
        permissions: USER_ROLE_PERMISSIONS.map((i) => i.value)
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.UPDATE_LOGS,
    label: '업데이트',
    active: pathname.includes(ROUTES.UPDATE_LOGS),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.UPDATE_LOGS,
        label: '업데이트',
        active: pathname.includes(ROUTES.UPDATE_LOGS),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.DOCS,
    label: '문서',
    active: pathname.includes(ROUTES.DOCS),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.DOCS,
        label: '문서',
        active: pathname.includes(ROUTES.DOCS),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.FAQ,
    label: 'FAQ',
    active: pathname.includes(ROUTES.FAQ),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.FAQ,
        label: 'FAQ',
        active: pathname.includes(ROUTES.FAQ),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.QA,
    label: '우리말 지식',
    active: pathname.includes(ROUTES.QA),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.QA,
        label: '우리말 지식',
        active: pathname.includes(ROUTES.QA),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.SUPPORT,
    label: '기술지원',
    active: pathname.includes(ROUTES.SUPPORT),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.SUPPORT,
        label: '기술지원',
        active: pathname.includes(ROUTES.SUPPORT),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.PRODUCT,
    label: '요금 안내',
    active: pathname.includes(ROUTES.PRODUCT),
    permissions: [],
    default: true,
    children: [
      {
        href: ROUTES.PRODUCT,
        label: '요금 안내',
        active: pathname.includes(ROUTES.PRODUCT),
        permissions: []
      }
    ]
  },
  {
    imgURL: Icons.update,
    href: ROUTES.ADMIN + ROUTES.DASHBOARD,
    label: '관리자',
    active: pathname.includes(ROUTES.ADMIN),
    permissions: ADMIN_PERMISSIONS.map((i) => i.value),
    default: false,
    hide: true,
    children: [
      {
        href: ROUTES.ADMIN + ROUTES.DASHBOARD,
        label: '대시보드',
        active: pathname.includes(ROUTES.DASHBOARD),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.USER_STATISTICS,
        label: '전체 통계',
        active: pathname.includes(ROUTES.USER_STATISTICS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value),
        children: [
          {
            href: ROUTES.ADMIN + ROUTES.USER_STATISTICS,
            label: '전체 통계',
            active: pathname.includes(ROUTES.USER_STATISTICS),
            permissions: ADMIN_PERMISSIONS.map((i) => i.value)
          },
          {
            href: ROUTES.ADMIN + ROUTES.USER_STATISTICS + ROUTES.USER_API,
            label: 'BareunAPI 통계',
            active: pathname.includes(ROUTES.USER_API),
            permissions: ADMIN_PERMISSIONS.map((i) => i.value)
          }
        ]
      },
      {
        href: ROUTES.ADMIN + ROUTES.USERS_MANAGE,
        label: '사용자 관리',
        active: pathname.includes(ROUTES.USERS_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.SUBSCRIPTIONS_MANAGE,
        label: '구독 관리',
        active: pathname.includes(ROUTES.SUBSCRIPTIONS_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.PAYMENTS_MANAGE,
        label: '결제 내역',
        active: pathname.includes(ROUTES.PAYMENTS_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.FAQ_MANAGE,
        label: 'FAQ 관리',
        active: pathname.includes(ROUTES.FAQ_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.NOTICES_MANAGE,
        label: '공지사항 관리',
        active: pathname.includes(ROUTES.NOTICES_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.BANNER_MESSAGES_MANAGE,
        label: '배너 메시지 관리',
        active: pathname.includes(ROUTES.BANNER_MESSAGES_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.AUDIT_LOGS,
        label: '감사 로그',
        active: pathname.includes(ROUTES.AUDIT_LOGS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.ERROR_LOGS,
        label: '에러 로그',
        active: pathname.includes(ROUTES.ERROR_LOGS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        // 검색 유입 회귀 점검 (#287) — 사이트맵 lastmod, 리다이렉트 계약, 색인 지시
        href: ROUTES.ADMIN + ROUTES.SEO_HEALTH,
        label: '검색 유입 점검',
        active: pathname.includes(ROUTES.SEO_HEALTH),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.SPELLER_REPORTS,
        label: '오류 제보 관리',
        active: pathname.includes(ROUTES.SPELLER_REPORTS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        // 전체 복사 직후 받은 별점·한 줄 평의 공개 여부를 정한다 (#291)
        href: ROUTES.ADMIN + ROUTES.SITE_REVIEWS,
        label: '별점·한 줄 평',
        active: pathname.includes(ROUTES.SITE_REVIEWS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        // 연구·교육 목적 무상 사용신청 심사 (#294)
        href: ROUTES.ADMIN + ROUTES.RESEARCH_APPLICATIONS,
        label: '연구목적 사용신청',
        active: pathname.includes(ROUTES.RESEARCH_APPLICATIONS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.API_USAGE_ANALYSIS,
        label: 'API 사용 분석',
        active: pathname.includes(ROUTES.API_USAGE_ANALYSIS),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      },
      {
        href: ROUTES.ADMIN + ROUTES.PUSH_MANAGE,
        label: '푸시 알림',
        active: pathname.includes(ROUTES.PUSH_MANAGE),
        permissions: ADMIN_PERMISSIONS.map((i) => i.value)
      }
    ]
  }
];

export const TABLE_DEFAULT_PARAMS = {
  page: '1'
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 0부터 시작하므로 1을 더해줌
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const tableDefaultParams = new URLSearchParams(TABLE_DEFAULT_PARAMS);

/**
 * 사용량 조회 기간 프리셋.
 * - last7: 최근 7일(기본)
 * - thisMonth: 이번 달 1일부터 오늘까지 — 아래 월별 표와 기간이 맞는다
 * - last30: 최근 30일 — 차트가 주 단위로 묶인다
 */
export type UsageRangePreset = 'last7' | 'thisMonth' | 'last30';

export const USAGE_RANGE_PRESETS: {
  key: UsageRangePreset;
  label: string;
}[] = [
  { key: 'last7', label: '최근 7일' },
  { key: 'thisMonth', label: '이번 달' },
  { key: 'last30', label: '최근 30일' }
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 프리셋에 해당하는 조회 기간을 돌려준다.
 *
 * ⚠️ `new Date()` 는 반드시 이 함수 안에서 부른다. 모듈 최상위에 두면 서버 컴포넌트가
 * 컨테이너 기동 시각으로 값을 굳혀버려, 배포한 지 오래될수록 최근 사용량이 조회 범위
 * 밖으로 밀려난다(#292).
 */
export function getUsageRange(preset: UsageRangePreset = 'last7'): {
  from: Date;
  to: Date;
} {
  const to = new Date();

  if (preset === 'thisMonth') {
    return { from: new Date(to.getFullYear(), to.getMonth(), 1), to };
  }
  if (preset === 'last30') {
    return { from: new Date(to.getTime() - 30 * DAY_MS), to };
  }
  return { from: new Date(to.getTime() - 7 * DAY_MS), to };
}

/** 조회 기간을 `yyyy-MM-dd` 문자열 쌍으로 돌려준다. */
export function getDateRangeDefaultParams(preset: UsageRangePreset = 'last7') {
  const { from, to } = getUsageRange(preset);
  return { from: formatDate(from), to: formatDate(to) };
}

/** 조회 기간을 URL 쿼리로 돌려준다. 호출할 때마다 오늘 기준으로 다시 계산된다. */
export function dateRangeDefaultParams(preset: UsageRangePreset = 'last7') {
  return new URLSearchParams(getDateRangeDefaultParams(preset));
}

/** 표(연/월) 기본 파라미터. 연도가 바뀌어도 따라가도록 호출 시점에 계산한다. */
export function getDateDefaultParams() {
  return {
    year: new Date().getFullYear().toString(),
    month: '0'
  };
}

export function dateDefaultParams() {
  return new URLSearchParams(getDateDefaultParams());
}
